// Everything a person sees before they are signed in.
//
// One component with a screen state rather than routes, because none of this is
// inside the application yet. Making them routes would mean the router deciding
// which paths are public, and that decision belongs in one place — the boot gate
// that renders this.
//
// Confirming an address and resetting a password happen with a six digit code
// typed into the page (the same flow the community screens use). One screen is
// still reached from an EMAIL: the invitation link carries a token in the query
// string, and the gate reads it and hands it in as the starting screen.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { authConfig, confirmResetCode, readInvitation, requestReset, resendCode, signIn, signUp, verifyCode } from './passwordSession';
import { useWorkspaceStore } from '../stores/workspaceStore';

/**
 * Mirrors MIN_PASSWORD on the server, and is only a HINT.
 *
 * The server is the authority and refuses a short password whether or not this
 * says so; showing the rule up front just saves a round trip. If the two ever
 * drift, the server's message is what the person ends up reading.
 */
const MIN_PASSWORD = 8;

export type Screen =
  | { kind: 'signIn' }
  | { kind: 'signUp' }
  | { kind: 'forgot' }
  | { kind: 'claimed' }
  /** The six digit code sent to the address, for a new account or a reset. */
  | { kind: 'code'; email: string; purpose: 'verify' | 'reset' }
  /** A reset's second half: the code has been typed, the password is next. */
  | { kind: 'newPassword'; email: string; code: string }
  | { kind: 'invite'; token: string };

export function AuthScreens({
  initial,
  onSignedIn,
}: {
  initial: Screen;
  onSignedIn: () => void;
}): JSX.Element {
  const [screen, setScreen] = useState<Screen>(initial);
  // Asked of the server, not read from the deployed config: registration is a
  // setting an administrator can change while this page is open. Undefined
  // until the answer arrives, so the link appears rather than flickering away.
  const [signupOffered, setSignupOffered] = useState(false);
  useEffect(() => {
    let live = true;
    authConfig()
      .then((c) => live && setSignupOffered(c.signup))
      .catch(() => {
        /* unreachable: offer nothing rather than a link that leads to a 403 */
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    // `m-auto` on the CHILD, not `items-center` on the parent. Flex centring
    // overflows in both directions once the content is taller than the
    // viewport, and the half that goes above the top is unreachable — no amount
    // of scrolling brings it back, so on a short window the heading is simply
    // gone. An auto margin centres the same way and yields at the edges.
    <div className="flex min-h-dvh justify-center bg-background px-4 py-8">
      <div className="m-auto w-full max-w-sm">
        {screen.kind === 'signIn' && (
          <SignIn onSignedIn={onSignedIn} onGo={setScreen} signupOffered={signupOffered} />
        )}
        {screen.kind === 'signUp' && <SignUp onGo={setScreen} />}
        {screen.kind === 'forgot' && <Forgot onGo={setScreen} />}
        {screen.kind === 'claimed' && <Claimed onGo={setScreen} />}
        {screen.kind === 'code' && <EnterCode email={screen.email} purpose={screen.purpose} onGo={setScreen} onSignedIn={onSignedIn} />}
        {screen.kind === 'newPassword' && <NewPassword email={screen.email} code={screen.code} onGo={setScreen} onSignedIn={onSignedIn} />}
        {screen.kind === 'invite' && <AcceptInvitation token={screen.token} onGo={setScreen} onSignedIn={onSignedIn} />}
      </div>
    </div>
  );
}

// ── shared chrome ───────────────────────────────────────────────────────────

function Heading({ title, blurb }: { title: string; blurb?: string }): JSX.Element {
  return (
    <div className="mb-5 space-y-1.5">
      <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
      {blurb && <p className="text-sm text-muted-foreground">{blurb}</p>}
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  ...props
}: { id: string; label: string; hint?: string } & React.ComponentProps<'input'>): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </label>
      <Input id={id} {...props} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** role="alert" so a failure that appears without navigation is announced. */
function Problem({ children }: { children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <p role="alert" className="text-sm text-destructive">
      {children}
    </p>
  );
}

function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-sm text-sm text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
    >
      {children}
    </button>
  );
}

/**
 * Submit handling, shared because every screen here gets it wrong the same way.
 *
 * Guards against a double submit, clears the previous error before trying
 * again, and leaves `busy` set on success — these screens navigate or swap
 * themselves out, so releasing the button would only offer a second submission
 * of something that already worked.
 */
function useSubmit(action: () => Promise<void>): {
  busy: boolean;
  error: string | null;
  submit: (e: FormEvent) => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    busy,
    error,
    submit: (e: FormEvent) => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      action().catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
        setBusy(false);
      });
    },
  };
}

// ── screens ─────────────────────────────────────────────────────────────────

function SignIn({
  onSignedIn,
  onGo,
  signupOffered,
}: {
  onSignedIn: () => void;
  onGo: (s: Screen) => void;
  signupOffered: boolean;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    await signIn(email, password);
    onSignedIn();
  });

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Heading title="Sign in" blurb="Use the email and password for this deployment." />
      <div className="space-y-3">
        <Field
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          required
          disabled={busy}
        />
        <Field
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          disabled={busy}
        />
      </div>
      <Problem>{error}</Problem>
      <Button type="submit" className="w-full" disabled={busy || !email || !password}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
      <div className="flex items-center justify-between">
        <TextLink onClick={() => onGo({ kind: 'forgot' })}>Forgot your password?</TextLink>
        {signupOffered && (
          <TextLink onClick={() => onGo({ kind: 'signUp' })}>Create an account</TextLink>
        )}
      </div>
    </form>
  );
}

function SignUp({ onGo }: { onGo: (s: Screen) => void }): JSX.Element {
  const [given, setGiven] = useState('');
  const [family, setFamily] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    const { claimed } = await signUp({
      email,
      password,
      givenName: given,
      familyName: family,
    });
    onGo(claimed ? { kind: 'claimed' } : { kind: 'code', email, purpose: 'verify' });
  });

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      {/* No promise of an email here. On an unclaimed deployment this signup IS
          the claim: the account is created verified and no link is ever sent,
          so pledging one would be wrong for exactly the person setting the
          deployment up. What actually happened is on the next screen, where it
          is known rather than predicted. */}
      <Heading title="Create an account" blurb="Tell us who you are and pick a password." />
      <div className="space-y-3">
        {/* NAME FIRST, and required. Colleagues have to recognise each other in
            a members list and on an annotation, and an account labelled only by
            its address makes both unreadable. Asked here rather than left to a
            profile screen people never open. */}
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="given-name"
            label="First name"
            value={given}
            onChange={(e) => setGiven(e.target.value)}
            autoComplete="given-name"
            autoFocus
            required
            disabled={busy}
          />
          <Field
            id="family-name"
            label="Last name"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            autoComplete="family-name"
            required
            disabled={busy}
          />
        </div>
        <Field
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          disabled={busy}
        />
        <Field
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          disabled={busy}
          hint={`At least ${MIN_PASSWORD} characters, and not one you have used elsewhere.`}
        />
      </div>
      <Problem>{error}</Problem>
      <Button
        type="submit"
        className="w-full"
        disabled={busy || !given.trim() || !family.trim() || !email || !password}
      >
        {busy ? 'Creating…' : 'Create account'}
      </Button>
      <TextLink onClick={() => onGo({ kind: 'signIn' })}>Back to sign in</TextLink>
    </form>
  );
}

/**
 * Accepting an invitation: create the account the link was sent to.
 *
 * The address is READ FROM THE TOKEN and shown rather than asked for. The
 * server checks the two against each other and refuses a mismatch, so a field
 * here would be a field whose only possible use is to get the form rejected —
 * and on a closed deployment it would look like the invitation had failed.
 *
 * The workspace is named because an invitation that does not say what you are
 * joining is a password prompt from a stranger.
 */
function AcceptInvitation({
  token,
  onGo,
  onSignedIn,
}: {
  token: string;
  onGo: (s: Screen) => void;
  onSignedIn: () => void;
}): JSX.Element {
  const [invite, setInvite] = useState<{ email: string; workspace: string } | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [given, setGiven] = useState('');
  const [family, setFamily] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    let live = true;
    readInvitation(token)
      .then((i) => live && setInvite(i))
      .catch((e: unknown) => live && setDead((e as Error).message));
    return () => {
      live = false;
    };
  }, [token]);

  const { busy, error, submit } = useSubmit(async () => {
    if (!invite) return;
    const { workspace } = await signUp({
      email: invite.email,
      password,
      givenName: given,
      familyName: family,
      invite: token,
    });
    // The worklist, like any other sign-in: enterprise makes no default
    // collection, and a newcomer has nowhere else to be sent. But the worklist
    // OF THE WORKSPACE THEY WERE INVITED TO — their personal one is still their
    // default, and it is empty. Chosen before signing in: the invitation is
    // spent now, so a sign-in that fails is retried from the sign-in screen,
    // and that must land there too.
    if (workspace) useWorkspaceStore.getState().setSelected(workspace);
    // The invitation proved the address, so there is no code to wait for.
    await signIn(invite.email, password);
    onSignedIn();
  });

  if (dead) {
    return (
      <div className="space-y-5">
        <Heading
          title="That invitation is no longer valid"
          blurb="It may have expired, been withdrawn, or already been used. Ask whoever invited you to send another."
        />
        <TextLink onClick={() => onGo({ kind: 'signIn' })}>Go to sign in</TextLink>
      </div>
    );
  }
  if (!invite) return <Heading title="Checking your invitation…" />;

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Heading
        title={`Join ${invite.workspace}`}
        blurb="Tell us your name and choose a password — the invitation already confirmed your address."
      />
      <div className="space-y-3">
        {/* Asked here too. Whoever invited them knows an address; the people
            they are about to work alongside need a name. */}
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="given-name"
            label="First name"
            value={given}
            onChange={(e) => setGiven(e.target.value)}
            autoComplete="given-name"
            autoFocus
            required
            disabled={busy}
          />
          <Field
            id="family-name"
            label="Last name"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            autoComplete="family-name"
            required
            disabled={busy}
          />
        </div>
        {/* Shown, not editable. The token names this address and the server
            refuses any other, so an input could only produce a refusal. */}
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-foreground">Email</span>
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {invite.email}
          </p>
        </div>
        <Field
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          disabled={busy}
          hint={`At least ${MIN_PASSWORD} characters, and not one you have used elsewhere.`}
        />
      </div>
      <Problem>{error}</Problem>
      <Button
        type="submit"
        className="w-full"
        disabled={busy || !given.trim() || !family.trim() || !password}
      >
        {busy ? 'Joining…' : 'Join'}
      </Button>
    </form>
  );
}

function Forgot({ onGo }: { onGo: (s: Screen) => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    await requestReset(email);
    onGo({ kind: 'code', email, purpose: 'reset' });
  });

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Heading
        title="Reset your password"
        blurb="Enter your address and we'll send you a code to choose a new password."
      />
      <Field
        id="email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="username"
        autoFocus
        required
        disabled={busy}
      />
      <Problem>{error}</Problem>
      <Button type="submit" className="w-full" disabled={busy || !email}>
        {busy ? 'Sending…' : 'Send the code'}
      </Button>
      <TextLink onClick={() => onGo({ kind: 'signIn' })}>Back to sign in</TextLink>
    </form>
  );
}

/**
 * Says the message was sent WITHOUT confirming the address exists.
 *
 * The server answers signup and reset identically whether or not an account is
 * there, precisely so neither can be used to discover who has one. Wording this
 * as "we've sent you a link" would give away exactly what that costs care to
 * hide, so it says what was done on our side and leaves the rest to the inbox.
 */
function Claimed({ onGo }: { onGo: (s: Screen) => void }): JSX.Element {
  return (
    <div className="space-y-5">
      <Heading
        title="Account created"
        blurb="Nobody had set this deployment up yet, so it is yours: you administer it, and your address is already confirmed. Sign in to continue."
      />
      <TextLink onClick={() => onGo({ kind: 'signIn' })}>Go to sign in</TextLink>
    </div>
  );
}

/**
 * The six digit code, one input per digit. Paste fills all six, Backspace
 * steps back. A verification code signs the person in when it is spent; a
 * reset code leads to the new password.
 */
function EnterCode({
  email,
  purpose,
  onGo,
  onSignedIn,
}: {
  email: string;
  purpose: 'verify' | 'reset';
  onGo: (s: Screen) => void;
  onSignedIn: () => void;
}): JSX.Element {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [note, setNote] = useState<string | null>(null);
  const joined = code.join('');
  const { busy, error, submit } = useSubmit(async () => {
    if (!/^\d{6}$/.test(joined)) throw new Error('Enter the 6-digit code from the email.');
    if (purpose === 'reset') {
      onGo({ kind: 'newPassword', email, code: joined });
      return;
    }
    await verifyCode(email, joined);
    // The worklist, like any other sign-in (see AcceptInvitation).
    onSignedIn();
  });
  const resend = async () => {
    setNote(null);
    try {
      await resendCode(email, purpose);
      setCode(['', '', '', '', '', '']);
      refs.current[0]?.focus();
      setNote(`If a code is due, a new one is on its way to ${email}`);
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not send another code.');
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Heading
        title="Check your email"
        blurb={
          // Hedged on purpose: the server answers a taken address the same way
          // it answers a new one, so this screen cannot promise a code went out.
          purpose === 'reset'
            ? `If ${email} has an account, we sent it a 6-digit code. Enter it below; it expires in ten minutes.`
            : `If ${email} is new here, we sent it a 6-digit code. Enter it below; it expires in ten minutes. Already have an account? Sign in instead.`
        }
      />
      <div className="flex justify-between gap-2">
        {code.map((v, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            value={v}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus={i === 0}
            disabled={busy}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const d = (e.target.value.match(/\d/g) || []).pop() || '';
              const next = [...code];
              next[i] = d;
              setCode(next);
              if (d && i < 5) refs.current[i + 1]?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !code[i] && i > 0) {
                const next = [...code];
                next[i - 1] = '';
                setCode(next);
                refs.current[i - 1]?.focus();
              }
            }}
            onPaste={(e) => {
              const t = (e.clipboardData.getData('text').match(/\d/g) || []).slice(0, 6);
              if (!t.length) return;
              e.preventDefault();
              const next = ['', '', '', '', '', ''];
              t.forEach((d, k) => { next[k] = d; });
              setCode(next);
              refs.current[Math.min(t.length, 5)]?.focus();
            }}
            className="h-14 w-12 rounded-md border bg-background text-center font-mono text-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        ))}
      </div>
      <Problem>{error}</Problem>
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
      <Button type="submit" className="w-full" disabled={busy || joined.length < 6}>
        {busy ? 'Checking…' : purpose === 'reset' ? 'Continue' : 'Verify email'}
      </Button>
      <p className="text-sm text-muted-foreground">
        Didn't get it? <TextLink onClick={resend}>Resend code</TextLink>
        {' · '}
        <TextLink onClick={() => onGo(purpose === 'reset' ? { kind: 'forgot' } : { kind: 'signUp' })}>Change email</TextLink>
      </p>
    </form>
  );
}

function NewPassword({
  email,
  code,
  onGo,
  onSignedIn,
}: {
  email: string;
  code: string;
  onGo: (s: Screen) => void;
  onSignedIn: () => void;
}): JSX.Element {
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    await confirmResetCode(email, code, password);
    onSignedIn();
  });

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Heading title="Choose a new password" blurb={`For ${email}`} />
      <Field
        id="password"
        label="New password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        autoFocus
        required
        disabled={busy}
        hint={`At least ${MIN_PASSWORD} characters, and not one you have used elsewhere.`}
      />
      <Problem>{error}</Problem>
      <Button type="submit" className="w-full" disabled={busy || !password}>
        {busy ? 'Saving…' : 'Save and sign in'}
      </Button>
      <TextLink onClick={() => onGo({ kind: 'signIn' })}>Back to sign in</TextLink>
    </form>
  );
}
